import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { countries } from './constants';
import { API_URL } from './authService';

function Destinations() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [destinationToDelete, setDestinationToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDestination, setViewingDestination] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const pageSize = 10;
  const [selectedFile, setSelectedFile] = useState(null);
  const { token } = useAuth();
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [isModalImageLoading, setIsModalImageLoading] = useState(false);

  const fetchDestinations = useCallback(async (page = 0, sort = '', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = sort ? `&sort=${sort}` : '';
      const searchParam = search ? `&${search}` : '';
      const response = await fetch(`${API_URL}/api/destinations?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const totalPages = Math.ceil(totalCount / pageSize);
      
      setDestinations(data);
      setTotalPages(totalPages);
      setTotalElements(totalCount);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDestinations(0, sortField ? `${sortField},${sortDirection}` : '');
  }, [fetchDestinations, sortField, sortDirection]);

useEffect(() => {
    let objectUrl;

    const fetchModalImage = async () => {
        if (!viewingDestination || !viewingDestination.id || !token) {
            setModalImageUrl('');
            return;
        }

        setIsModalImageLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/destinations/${viewingDestination.id}/image`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok && response.headers.get('Content-Type')?.startsWith('image')) {
                const imageBlob = await response.blob();
                objectUrl = URL.createObjectURL(imageBlob);
                setModalImageUrl(objectUrl);
            } else {
                setModalImageUrl('');
            }
        } catch (error) {
            console.error('Error fetching modal image:', error);
            setModalImageUrl('');
        } finally {
            setIsModalImageLoading(false);
        }
    };

    fetchModalImage();

    return () => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
    };
  }, [viewingDestination, token]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    const sortParam = sortField ? `${sortField},${sortDirection}` : '';
    const searchParam = buildSearchParam();
    fetchDestinations(newPage, sortParam, searchParam);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    setLoading(true);
    const searchParam = buildSearchParam();
    fetchDestinations(0, `${field},${newDirection}`, searchParam);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue.trim()) return '';
    
    const fieldMap = {
      'destinationName': 'destinationName.contains',
      'country': 'country.contains'
    };
    
    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchDestinations(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (searchField && searchValue.trim()) {
      const timeoutId = setTimeout(() => {
        setLoading(true);
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchDestinations(0, sortParam, searchParam);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (searchField && !searchValue.trim()) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchDestinations(0, sortParam);
    }
  }, [searchValue, searchField, sortField, sortDirection]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    country: '',
    destinationName: '',
    destinationImageUrl: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/jpg')) {
        setSelectedFile(file);
    } else {
        alert('Por favor, selecciona solo archivos .jpg o .jpeg');
        e.target.value = null; // Clear the input
        setSelectedFile(null);
    }
  };

  const createDestination = async (destinationData) => {
    const response = await fetch(`${API_URL}/api/destinations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(destinationData)
    });
    if (!response.ok) {
        const errorText = await response.text();
        console.error("Creation failed:", errorText);
        throw new Error('No se pudo crear el destino.');
    }
    return response.json();
  };

  const updateDestination = async (id, destinationData) => {
    const response = await fetch(`${API_URL}/api/destinations/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(destinationData)
    });
    if (!response.ok) {
        const errorText = await response.text();
        console.error("Update failed:", errorText);
        throw new Error('No se pudo actualizar el destino.');
    }
    return response.json();
  };

  const deleteDestination = async (id) => {
    await fetch(`${API_URL}/api/destinations/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  };

  const uploadImage = async (destinationId, file) => {
    const formData = new FormData();
    // El backend debe estar preparado para recibir un archivo con el nombre 'file'
    formData.append('file', file);

    try {
        const response = await fetch(`${API_URL}/api/destinations/${destinationId}/upload-image`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error('Error al subir la imagen.');
        }
        console.log('Imagen subida exitosamente para el destino:', destinationId);
    } catch (error) {
        console.error('Error en la subida de imagen:', error);
        alert('El destino se guardó, pero hubo un error al subir la imagen.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let savedDestination; // This will hold the destination object after the first save.

      if (editingId) {
        // --- Lógica para ACTUALIZAR ---
        const payload = { ...formData };
        // Si se está subiendo un nuevo archivo, actualizamos la URL en el payload.
        if (selectedFile) {
          payload.destinationImageUrl = `${API_URL}/api/destinations/${editingId}/image`;
        }
        savedDestination = await updateDestination(editingId, payload);
        
        // Si había un archivo seleccionado, lo subimos.
        if (selectedFile) {
          await uploadImage(editingId, selectedFile);
        }

      } else {
        // --- Lógica para CREAR ---
        // 1. Creamos el destino sin la URL de la imagen para poder obtener un ID.
        const { destinationImageUrl, ...initialPayload } = formData;
        savedDestination = await createDestination(initialPayload);

        // Si también se seleccionó un archivo...
        if (selectedFile && savedDestination && savedDestination.id) {
          // 2. Subimos la imagen.
          await uploadImage(savedDestination.id, selectedFile);

          // 3. Actualizamos el destino recién creado para guardarle la URL de la imagen.
          // Se usa 'initialPayload' para asegurar que todos los datos (nombre, país) se envíen en la actualización.
          const finalPayload = { 
            ...initialPayload,
            id: savedDestination.id,
            destinationImageUrl: `${API_URL}/api/destinations/${savedDestination.id}/image`
          };
          await updateDestination(savedDestination.id, finalPayload);
        }
      }

      resetForm();
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchDestinations(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error saving destination:', error);
      alert(`Error al guardar el destino: ${error.message}`);
    }
  };

  const handleEdit = (destination) => {
    setFormData(destination);
    setEditingId(destination.id);
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleView = (destination) => {
    setViewingDestination(destination);
    setShowViewModal(true);
  };

  const handleDelete = (id) => {
    setDestinationToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDestination(destinationToDelete);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchDestinations(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error deleting destination:', error);
    }
    setShowDeleteModal(false);
    setDestinationToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDestinationToDelete(null);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingDestination(null);
  };

  const resetForm = () => {
    setFormData({
      country: '',
      destinationName: '',
      destinationImageUrl: ''
    });
    setShowForm(false);
    setEditingId(null);
    setSelectedFile(null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Destinos</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={styles.addButton}>
          + Agregar Destino
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select 
            value={searchField} 
            onChange={handleSearchFieldChange} 
            style={styles.searchSelect}
          >
            <option value="">Seleccionar campo</option>
            <option value="destinationName">Nombre</option>
            <option value="country">País</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              placeholder={`Buscar por ${searchField === 'destinationName' ? 'nombre' : 'país'}...`}
              style={styles.searchInput}
            />
          </div>
        )}
        {(searchField || searchValue) && (
          <button 
            onClick={() => {
              setSearchField('');
              setSearchValue('');
              setCurrentPage(0);
              setLoading(true);
              const sortParam = sortField ? `${sortField},${sortDirection}` : '';
              fetchDestinations(0, sortParam);
            }}
            style={styles.clearButton}
          >
            Limpiar
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Destino' : 'Nuevo Destino'}</h3>
          <div style={styles.formGrid}>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Nombre del Destino</label>
              <input name="destinationName" value={formData.destinationName} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>País</label>
              <select name="country" value={formData.country} onChange={handleInputChange} style={styles.select} required>
                <option value="">Seleccionar País</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div style={{...styles.fieldContainer, gridColumn: '1 / -1'}}>
              <label style={styles.label}>Imagen del Destino (JPG)</label>
              <input type="file" accept=".jpg, .jpeg" onChange={handleFileChange} style={styles.input} />
            </div>
          </div>
          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando destinos...</div>
        ) : destinations.length === 0 ? (
          <div style={styles.emptyState}>
            No hay destinos registrados. ¡Agrega el primero!
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('destinationName')}>Destino{getSortArrow('destinationName')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('country')}>País{getSortArrow('country')}</th>
                  <th style={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {destinations.map(destination => (
                  <tr key={destination.id}>
                    <td style={styles.tableCell}>{destination.destinationName}</td>
                    <td style={styles.tableCell}>{destination.country}</td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleView(destination)} style={styles.viewButton}>Ver</button>
                        <button onClick={() => handleEdit(destination)} style={styles.editButton}>Editar</button>
                        <button onClick={() => handleDelete(destination.id)} style={styles.deleteButton}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} destinos
              </div>
              <div style={styles.paginationControls}>
                <button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 0}
                  style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Anterior
                </button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage >= totalPages - 1}
                  style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingDestination && (
        <div style={styles.modalOverlay}>
          <div style={styles.viewModal}>
            <h3 style={styles.modalTitle}>Información del Destino</h3>
            <div style={styles.viewContent}>
              <div style={styles.viewRow}><strong>Nombre del Destino:</strong> {viewingDestination.destinationName}</div>
              <div style={styles.viewRow}><strong>País:</strong> {viewingDestination.country}</div>
              <div style={styles.viewRow}>
                <strong>Imagen:</strong>
                <div style={{ marginTop: '10px' }}>
                  {isModalImageLoading ? (
                    <p>Cargando imagen...</p>
                  ) : modalImageUrl ? (
                    <img 
                      src={modalImageUrl} 
                      alt={`Imagen de ${viewingDestination.destinationName}`} 
                      style={{ maxWidth: '100%', height: 'auto', border: '2px solid #ddd', borderRadius: '8px' }}
                    />
                  ) : (
                    <p>No hay imagen disponible.</p>
                  )}
                </div>
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button onClick={closeViewModal} style={styles.modalCancelButton}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro de que deseas eliminar este destino?</p>
            <div style={styles.modalButtons}>
              <button onClick={cancelDelete} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
  addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
  fieldContainer: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  select: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', width: '180px', minWidth: '180px', fontSize: '18px' },
  sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '18px' },
  tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
  actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
  editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
  paginationInfo: { fontSize: '18px', color: '#6c757d' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
  paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
  paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
  pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
  modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
  modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '500px', width: '90%' },
  viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
  searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
  searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
  searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
  searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
  searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
  clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' }
}

export default Destinations;